import type { components } from '@/generated/internal-api/openapi-types';
import type { AnimalAlertV1 } from './Alert.model';
import type { Cage } from './Cage.model';
import type { Entity, ID, ISODate, ISODateTime } from './Common.model';
import type { LatestMeasurement } from './LatestMeasurements.model';
import type { TreatmentGroup } from './TreatmentGroup.model';
import { Study, StudyApiId, UserAccessType } from './Study.model';
import { UserApiId } from '@/model/User.model';
import { AnimalDefinitionApiId } from '@/model/PlannedAnimal.model';
import { Observation } from '@/model/Observation.model';

export interface AnimalMetadata extends Entity {
  field_type: string;
  glossary_id: number;
  options?: Array<unknown>;
  read_only?: unknown;
  title: string;
  value: string;
}

export interface AnimalSpecies extends Entity {
  active: number;
  deleted_at?: string;
  name: string;
  team_id: number;
  strains: Array<AnimalStrain>;
}

export interface AnimalStrain extends Entity {
  active?: number;
  attached: boolean;
  deleted_at?: string;
  name: string;
  species: AnimalSpecies;
  species_id?: number;
}

export interface KeyedLatestMeasurement {
  [calculation: string]: {
    measured_at: ISODate;
    name: string;
    subject_id: ID;
    value: string;
    value_change: string;
    value_change_from_first: string;
  };
}

export type PreviousStudy = {
  study: { id: number; api_id: StudyApiId; name: string; code?: string; access?: UserAccessType };
  moved: { at: ISODateTime; by: { id: number; api_id: UserApiId; name: string } };
};
export type AnimalApiId = components['schemas']['AnimalApiId.schema'];
export type SubjectApiId = AnimalApiId;

export type AltId = components['schemas']['AnimalAltIdField.schema'];

type AltIds = Partial<Record<AltId, string>>;

export type AnimalIdentifier = 'name' | AltId;

export type DiseaseInductedAtUpdateRequestV1 = components['schemas']['DiseaseInductedAtUpdateRequestV1.schema'];
export type AnimalV1 = components['schemas']['AnimalV1.schema'];
export type AnimalDefinitionV1 = components['schemas']['AnimalDefinitionV1.schema'];
export type AnimalSex = components['schemas']['AnimalSex.schema'];

export const AnimalSexMap = {
  m: 'Male',
  f: 'Female',
  u: 'Unknown',
} as const satisfies Record<AnimalSex, string>;

export type AnimalSexLabel = (typeof AnimalSexMap)[keyof typeof AnimalSexMap];

export interface Animal extends Entity {
  api_id?: AnimalApiId;
  alt_ids?: AltIds;
  alerts?: Array<AnimalAlertV1>;
  animal_selection?: { next?: number; previous?: number };
  cage?: Cage;
  cage_id?: Cage['id'];
  catalog?: string;
  collection_id?: ID;
  deceasedCageId?: Cage['id'];
  dob: string | null;
  metadata: Array<AnimalMetadata>;
  number?: number;
  sex: AnimalSex;
  species?: AnimalSpecies;
  strain?: AnimalStrain;
  species_name?: string;
  strain_name?: string;
  study_group_id?: ID;
  study_group?: TreatmentGroup;
  study_id?: ID;
  study_api_id?: StudyApiId;
  study?: Study;
  name?: string;
  terminated_at?: ISODate;
  disease_survivor?: boolean;
  disease_inducted_at?: ISODate;
  tracking_started_at?: ISODate;
  terminated_at_data?: {
    date: ISODate;
    email: string;
    glossary_id: ID;
    id: ID;
    name: string;
    termination_other_reason: string | null;
    termination_title: string;
    termination_value: string;
  };
  collection?: {
    name: string;
  };
  latestMeasurement?: Record<string, LatestMeasurement>;
  latest_observed_at?: ISODateTime;
  latest_observations?: Array<Observation>;
  significantMeasurement?: Record<string, LatestMeasurement>;
  measurementOnDate?: Record<string, LatestMeasurement>;
  previous_studies?: Array<PreviousStudy>;
  animal_definition_api_id?: AnimalDefinitionApiId;
  external_id?: string;
  external_instance_id?: string;
  external_termination_url?: string;
  external_batch_id?: string;
}

export type DeceasedAnimal = Animal & Required<Pick<Animal, 'terminated_at'>>;

export type OperationAnimalsMoveV1Request = components['schemas']['OperationAnimalsMoveV1.schema'];

export type AnimalListExistenceMeta = Pick<components['schemas']['AnimalV1List.schema'], 'animals_exist_in_study'>;
